import { getEntity } from '@/backend/entity'
import { isAllAvailable, type Eventually } from '@/data/clientUtils'
import { useEntityStore } from '@/data/useEntityStore'
import { invariant } from '@/shared/utils/typeAssertions'
import { useQuery } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'

/**
 * Fetches a single entity
 */
export function useEntityQuery(props: {
  workspaceId: Eventually<string>
  projectId: Eventually<string>
  entityId: Eventually<string>
  staleTime?: number
  disabled?: MaybeRefOrGetter<boolean>
}) {
  const entityStore = useEntityStore()

  const query = useQuery({
    staleTime: props.staleTime,
    enabled: isAllAvailable([
      () => props.workspaceId,
      () => props.projectId,
      () => props.entityId,
      () => !toValue(props.disabled),
    ]),
    queryKey: [props.workspaceId, props.projectId, 'entity', props.entityId],
    queryFn: async () => {
      const workspaceId = toValue(props.workspaceId)
      const projectId = toValue(props.projectId)
      const entityId = toValue(props.entityId)
      invariant(workspaceId && projectId && entityId)

      const result = await getEntity(workspaceId, projectId, entityId)

      if (!result.ok) {
        throw new Error('Failed to fetch entity', { cause: result.error })
      }

      entityStore.pushEntityJSON(result.data, workspaceId)
      return result.data
    },
  })

  const entity = computed(() => {
    if (!query.data.value) return undefined
    return entityStore.getFullEntity(query.data.value.id)
  })

  return { ...query, entity }
}
